/**
 * VERONI collection finder — gemstone + gold picker.
 * Matches products AND the closest variant image/price/URL so the shortlist
 * actually looks like Aquamarine · Rose Gold (not a default yellow/amethyst).
 *
 * The Liquid seed is only the first page of the collection (~50). After boot
 * we page /collections/{handle}/products.json so later products (Tecla,
 * Primavera, etc.) are not hidden.
 */
(function () {
  const root = document.querySelector('[data-veroni-finder]');
  if (!root) return;

  const catalogEl = root.querySelector('[data-veroni-finder-catalog]');
  const gemAssetsEl = root.querySelector('[data-veroni-finder-gem-assets]');
  let catalog = [];
  let gemAssets = {};
  try {
    catalog = JSON.parse(catalogEl?.textContent || '[]').filter((p) => p && p.url);
  } catch (e) {
    catalog = [];
  }
  try {
    gemAssets = JSON.parse(gemAssetsEl?.textContent || '{}');
  } catch (e) {
    gemAssets = {};
  }

  const type = root.dataset.collectionType || 'pieces';
  const steps = {
    intro: root.querySelector('[data-finder-step="intro"]'),
    gem: root.querySelector('[data-finder-step="gem"]'),
    gold: root.querySelector('[data-finder-step="gold"]'),
    results: root.querySelector('[data-finder-step="results"]'),
  };
  const gemMount = root.querySelector('[data-finder-gems]');
  const goldMount = root.querySelector('[data-finder-golds]');
  const picksMount = root.querySelector('[data-finder-picks]');
  const liveEl = root.querySelector('[data-finder-live]');
  const liveStatus = root.querySelector('[data-finder-live-status]');
  const chipsMount = root.querySelector('[data-finder-chips]');
  const resultTitle = root.querySelector('[data-finder-result-title]');
  const resultSub = root.querySelector('[data-finder-result-sub]');
  const resultKicker = root.querySelector('[data-finder-result-kicker]');
  const grid = document.querySelector('[data-veroni-finder-grid]');

  const state = { gem: null, gold: null };

  const GEM_HEX = {
    ruby: '#A83246',
    emerald: '#2F8F66',
    sapphire: '#2F5389',
    amethyst: '#9B6BB0',
    'blue topaz': '#57A6C9',
    citrine: '#D99F38',
    garnet: '#8A2C3B',
    peridot: '#9BAD4F',
    diamond: '#E6E1D7',
    pearl: '#F2EDE4',
    'white topaz': '#E8E4DA',
    'green amethyst': '#7FA86F',
    'pink topaz': '#C9748E',
    'pink sapphire': '#C9748E',
    aquamarine: '#8FC6CB',
    tanzanite: '#6355A8',
    opal: '#E8DFEA',
    morganite: '#E8B4A8',
    onyx: '#2B2B2B',
    'black spinel': '#202124',
    'black pearl': '#3A3A44',
    alexandrite: '#5C6FA8',
    spinel: '#6B3A5C',
    'lemon quartz': '#D6CD35',
    'rose quartz': '#E4B8BE',
    'smoky quartz': '#8A735A',
    'green sapphire': '#4F7A4F',
    'green corundum': '#4F7A4F',
    'black onyx': '#2B2B2B',
    'blue chalcedony': '#9BB8D4',
    'green chalcedony': '#8FB89A',
    'pink chalcedony': '#E5B6C0',
    'red chalcedony': '#C45C58',
    'clear amethyst': '#D4C6DC',
    zirconia: '#D8D4CC',
    'cubic zirconia': '#D8D4CC',
    cz: '#D8D4CC',
  };

  const GOLD_META = {
    'yellow gold': { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: 'Yellow' },
    yellow: { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: 'Yellow' },
    'white gold': { className: 'v-finder__metal--white', hex: '#e8e8e6', short: 'White' },
    white: { className: 'v-finder__metal--white', hex: '#e8e8e6', short: 'White' },
    'rose gold': { className: 'v-finder__metal--rose', hex: '#e5b7a3', short: 'Rose' },
    rose: { className: 'v-finder__metal--rose', hex: '#e5b7a3', short: 'Rose' },
  };

  const GEM_SLUGS = [
    ['green-amethyst', 'Green Amethyst'],
    ['blue-topaz', 'Blue Topaz'],
    ['white-topaz', 'White Topaz'],
    ['pink-topaz', 'Pink Topaz'],
    ['pink-sapphire', 'Pink Sapphire'],
    ['black-pearl', 'Black Pearl'],
    ['black-onyx', 'Black Onyx'],
    ['rose-quartz', 'Rose Quartz'],
    ['smoky-quartz', 'Smoky Quartz'],
    ['green-sapphire', 'Green Sapphire'],
    ['green-corundum', 'Green Sapphire'],
    ['blue-chalcedony', 'Blue Chalcedony'],
    ['green-chalcedony', 'Green Chalcedony'],
    ['pink-chalcedony', 'Pink Chalcedony'],
    ['red-chalcedony', 'Red Chalcedony'],
    ['clear-amethyst', 'Clear Amethyst'],
    ['alexandrite', 'Alexandrite'],
    ['aquamarine', 'Aquamarine'],
    ['amethyst', 'Amethyst'],
    ['sapphire', 'Sapphire'],
    ['tanzanite', 'Tanzanite'],
    ['emerald', 'Emerald'],
    ['citrine', 'Citrine'],
    ['garnet', 'Garnet'],
    ['peridot', 'Peridot'],
    ['diamond', 'Diamond'],
    ['cubic-zirconia', 'Cubic Zirconia'],
    ['zirconia', 'Cubic Zirconia'],
    ['cz', 'Cubic Zirconia'],
    ['lemon-quartz', 'Lemon Quartz'],
    ['morganite', 'Morganite'],
    ['ruby', 'Ruby'],
    ['pearl', 'Pearl'],
    ['opal', 'Opal'],
    ['onyx', 'Onyx'],
    ['black-spinel', 'Black Spinel'],
    ['spinel', 'Spinel'],
    ['topaz', 'Topaz'],
  ];

  function normalize(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function unique(list) {
    const seen = new Set();
    const out = [];
    (list || []).forEach((v) => {
      const t = String(v || '').trim();
      if (!t) return;
      const key = normalize(t);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(t);
    });
    return out.sort((a, b) => a.localeCompare(b));
  }

  /** Catalog option values are often "Ruby & Diamond". Collapse those to
   *  the colored stone so the picker is one chip per family, not 50 rows. */
  function gemKey(label) {
    let key = normalize(label)
      .replace(/^(?:lab[-\s]?grown\s+)/, '')
      .replace(/^purple\s+/, '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim();
    if (key === 'zirconia' || key === 'cz' || key === 'cubic zirconia') return 'cubic zirconia';
    if (key === 'solid gold' || key === 'gold') return '';
    return key;
  }

  function familiesFrom(label) {
    return String(label || '')
      .split(/\s*(?:&|\band\b|\+|\/)\s*/i)
      .map(gemKey)
      .filter(Boolean);
  }

  function coloredFamilies(label) {
    const parts = familiesFrom(label);
    const colored = parts.filter((p) => p !== 'diamond' && p !== 'cz' && p !== 'cubic zirconia');
    return colored.length ? colored : parts;
  }

  function displayGemLabel(key) {
    const slug = String(key || '').replace(/\s+/g, '-');
    const hit = GEM_SLUGS.find(([s]) => s === slug);
    if (hit) return hit[1];
    return String(key || '')
      .split(' ')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
      .join(' ');
  }

  function optionHasFamily(optionLabel, family) {
    const fam = gemKey(family);
    if (!fam) return false;
    const parts = familiesFrom(optionLabel);
    if (fam === 'diamond') {
      return parts.includes('diamond') && parts.every((p) => p === 'diamond');
    }
    // Catalog mixes "Spinel" and "Black Spinel" for the same sold family.
    if (fam === 'black spinel' || fam === 'spinel') {
      return parts.some((part) => part === 'spinel' || part === 'black spinel');
    }
    if (fam === 'cubic zirconia' || fam === 'zirconia' || fam === 'cz') {
      return parts.some((part) => part === 'cubic zirconia' || part === 'zirconia' || part === 'cz');
    }
    return parts.includes(fam);
  }

  function optionMatches(opts, target) {
    if (!target) return true;
    const t = normalize(target);
    if (!t) return true;
    const tCore = t.replace(/\b\d+k\b/g, '').replace(/\bgold\b/g, '').trim();
    return (opts || []).some((o) => {
      const n = normalize(o);
      if (!n) return false;
      if (n === t || n.includes(t) || t.includes(n)) return true;
      const nCore = n.replace(/\b\d+k\b/g, '').replace(/\bgold\b/g, '').trim();
      return !!(tCore && nCore && (nCore === tCore || nCore.includes(tCore) || tCore.includes(nCore)));
    });
  }

  function optionsFromVariant(v) {
    const out = [];
    if (v.option1) out.push(String(v.option1));
    if (v.option2) out.push(String(v.option2));
    if (v.option3) out.push(String(v.option3));
    if (out.length) return out;
    if (Array.isArray(v.options) && v.options.length) return v.options.map(String);
    if (v.title && v.title !== 'Default Title' && v.title.indexOf('/') !== -1) {
      return v.title
        .split('/')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    // Filename fallback: …-aquamarine-rose-1.webp
    // Gold must match the trailing token (…-rose-1.jpg) so names like "laura-rose-…"
    // are not misread as Rose Gold.
    const img = v.image || '';
    if (!img) return out;
    const file = img.split('/').pop().split('?')[0].toLowerCase();
    let gem = '';
    for (const [slug, label] of GEM_SLUGS) {
      if (file.includes(slug)) {
        gem = label;
        break;
      }
    }
    let gold = '';
    const goldTail = file.match(/-(yellow|white|rose)(?:-gold)?(?:-\d+)?\.(?:webp|jpe?g|png)$/);
    if (goldTail) {
      const g = goldTail[1];
      gold = g === 'yellow' ? 'Yellow Gold' : g === 'white' ? 'White Gold' : 'Rose Gold';
    }
    if (gem) out.push(gem);
    if (gold) out.push(gold);
    return out;
  }

  function scoreVariant(v, gem, gold) {
    const opts = optionsFromVariant(v);
    const gemN = normalize(gem);
    const goldN = normalize(gold);
    // Gemstone names must be exact: "Amethyst" must never match
    // "Green Amethyst", and "Topaz" must not absorb every colored topaz.
    // Variant option values and finder choices come from the same catalog
    // source, so normalized equality is both sufficient and deterministic.
    const hasGem = gemN ? opts.some((option) => optionHasFamily(option, gemN)) : false;
    const hasGold = goldN ? optionMatches(opts, goldN) : false;
    if (gemN && goldN) {
      if (hasGem && hasGold) return 120;
      if (hasGem) return 70;
      if (hasGold) return 40;
      return 0;
    }
    if (gemN) return hasGem ? 90 : 0;
    if (goldN) return hasGold ? 80 : 0;
    return 20;
  }

  function bestVariant(product, gem, gold) {
    const variants = product.variants || [];
    if (!variants.length) {
      return {
        id: null,
        image: product.image,
        price: product.price,
        options: [],
        score: gem || gold ? 0 : 20,
      };
    }
    let best = variants[0];
    let bestScore = -1;
    for (const v of variants) {
      const s = scoreVariant(v, gem, gold);
      if (s > bestScore) {
        bestScore = s;
        best = v;
      }
    }
    return {
      id: best.id,
      image: best.image || product.image,
      price: best.price || product.price,
      options: optionsFromVariant(best),
      score: Math.max(bestScore, 0),
    };
  }

  function productMatches(p) {
    // Prefer products that have a true gem+gold variant hit
    const hit = bestVariant(p, state.gem, state.gold);
    if (state.gem && state.gold) return hit.score >= 100;
    if (state.gem) return hit.score >= 70;
    if (state.gold) return hit.score >= 40;
    return true;
  }

  function allGems() {
    const seen = new Set();
    const out = [];
    catalog.forEach((p) => {
      (p.gems || []).forEach((g) => {
        coloredFamilies(g).forEach((fam) => {
          if (!fam || fam === 'solid gold' || fam === 'alex' || fam === 'rite') return;
          const label = displayGemLabel(fam);
          if (!label || !label.trim()) return;
          if (seen.has(fam)) return;
          seen.add(fam);
          out.push(label);
        });
        const parts = familiesFrom(g);
        if (parts.length === 1 && parts[0] === 'diamond' && !seen.has('diamond')) {
          seen.add('diamond');
          out.push('Diamond');
        }
      });
    });
    return out.sort((a, b) => a.localeCompare(b));
  }
  function allGolds() {
    return unique(catalog.flatMap((p) => p.golds || []));
  }

  function gemHex(label) {
    return GEM_HEX[normalize(label)] || '#E6E1D7';
  }

  function gemParts(label) {
    const parts = String(label || '')
      .split(/\s*&\s*/)
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.length ? parts.slice(0, 3) : [String(label || '')];
  }

  function gemAsset(label) {
    let key = normalize(label);
    if (key === 'blue sapphire') key = 'sapphire';
    if (key === 'zirconia' || key === 'cz' || key === 'cubic zirconia') key = 'cubic zirconia';
    if (key === 'clear topaz') key = 'white topaz';
    if (key === 'green corundum') key = 'green sapphire';
    if (key === 'onyx') key = 'black onyx';
    if (key === 'smokey quartz') key = 'smoky quartz';
    if (/^multi(?:-|\s)?(?:stone|gemstone)$/.test(key)) key = 'multi-gemstone';
    return gemAssets[key] || '';
  }

  function gemVisualMarkup(label, compact) {
    const parts = gemParts(label);
    const segments = parts
      .map((part) => {
        const asset = gemAsset(part);
        const title = escapeAttr(part);
        if (asset) {
          return `<span class="v-finder__gemstone-segment" title="${title}"><img src="${escapeAttr(asset)}" alt="" width="48" height="48" loading="lazy" decoding="async"></span>`;
        }
        return `<span class="v-finder__gemstone-segment" title="${title}" style="background:${gemHex(part)}"></span>`;
      })
      .join('');
    return `<span class="v-finder__gemstone${compact ? ' v-finder__gemstone--compact' : ''}" data-stone-count="${parts.length}" aria-hidden="true">${segments}</span>`;
  }

  function goldMeta(label) {
    return GOLD_META[normalize(label)] || { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: label };
  }

  function scrollToNext(el) {
    if (!el || el.hidden) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const header = document.querySelector('header, .header-group, sticky-header');
        const offset = header ? header.getBoundingClientRect().height + 12 : 16;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    });
  }

  function showStep(name) {
    if (name === 'gem' || name === 'gold') {
      if (steps.intro) steps.intro.hidden = true;
      if (steps.results) steps.results.hidden = true;
      if (steps.gem) {
        steps.gem.hidden = false;
        steps.gem.classList.add('is-active', 'is-enter');
      }
      if (steps.gold) {
        const showGold = name === 'gold' || !!state.gem;
        steps.gold.hidden = !showGold;
        steps.gold.classList.toggle('v-finder__panel--inline', showGold);
        if (showGold) steps.gold.classList.add('is-active', 'is-enter');
      }
      root.dataset.step = name;
      return;
    }
    Object.entries(steps).forEach(([key, el]) => {
      if (!el) return;
      const on = key === name;
      el.hidden = !on;
      el.classList.toggle('is-active', on);
      if (on) {
        el.classList.remove('is-enter');
        void el.offsetWidth;
        el.classList.add('is-enter');
      }
    });
    root.dataset.step = name;
  }

  function gemButton(label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'v-finder__choice v-finder__choice--gem';
    btn.setAttribute('aria-label', label);
    btn.innerHTML = `
      ${gemVisualMarkup(label, false)}
      <span class="v-finder__choice-label">${escapeHtml(label)}</span>
    `;
    btn.addEventListener('click', () => {
      state.gem = label;
      markSelected(gemMount, label);
      showStep('gold');
      renderGolds();
      updateLive(6);
      scrollToNext(steps.gold);
    });
    return btn;
  }

  function goldButton(label) {
    const meta = goldMeta(label);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'v-finder__choice v-finder__choice--metal';
    btn.setAttribute('aria-label', label);
    btn.innerHTML = `
      <span class="v-finder__metal ${meta.className}" style="background:${meta.hex}"></span>
      <span class="v-finder__choice-label">${escapeHtml(label)}</span>
    `;
    btn.addEventListener('click', () => {
      state.gold = label;
      markSelected(goldMount, label);
      updateLive(0);
      scrollToNext(liveEl);
    });
    return btn;
  }

  function renderGems() {
    if (!gemMount) return;
    gemMount.replaceChildren();
    const gems = allGems();
    if (!gems.length) {
      state.gem = null;
      showStep('gold');
      renderGolds();
      return;
    }
    gems.forEach((g) => gemMount.appendChild(gemButton(g)));
  }

  function renderGolds() {
    if (!goldMount) return;
    goldMount.replaceChildren();
    const golds = allGolds();
    if (!golds.length) {
      state.gold = null;
      finish();
      return;
    }
    // Prefer canonical order yellow → white → rose
    const order = ['yellow gold', 'white gold', 'rose gold'];
    golds
      .slice()
      .sort((a, b) => {
        const ia = order.indexOf(normalize(a));
        const ib = order.indexOf(normalize(b));
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .forEach((g) => goldMount.appendChild(goldButton(g)));
    if (state.gold) markSelected(goldMount, state.gold);
  }

  function markSelected(mount, label) {
    if (!mount) return;
    const want = normalize(label);
    mount.querySelectorAll('.v-finder__choice').forEach((btn) => {
      const text = normalize(btn.getAttribute('aria-label') || btn.textContent);
      btn.classList.toggle('is-selected', text === want || text.indexOf(want) !== -1);
    });
  }

  function renderChips() {
    if (!chipsMount) return;
    chipsMount.replaceChildren();
    const chips = [];
    if (state.gem) {
      chips.push({ label: state.gem, hex: gemHex(state.gem), gemstone: true });
    }
    if (state.gold) {
      chips.push({ label: state.gold, hex: goldMeta(state.gold).hex });
    }
    if (!chips.length) {
      chipsMount.hidden = true;
      return;
    }
    chipsMount.hidden = false;
    chips.forEach((c) => {
      const el = document.createElement('span');
      el.className = 'v-finder__chip';
      el.innerHTML = `${c.gemstone ? gemVisualMarkup(c.label, true) : `<i style="background:${c.hex}"></i>`}${escapeHtml(c.label)}`;
      chipsMount.appendChild(el);
    });
  }

  function rankedMatches() {
    return catalog
      .map((p) => {
        const hit = bestVariant(p, state.gem, state.gold);
        return { product: p, hit };
      })
      .filter(({ hit }) => {
        if (state.gem && state.gold) return hit.score >= 100;
        if (state.gem) return hit.score >= 70;
        if (state.gold) return hit.score >= 40;
        return true;
      })
      .sort((a, b) => b.hit.score - a.hit.score);
  }

  function updateLive(limit) {
    const ranked = rankedMatches();
    const total = ranked.length;
    const cap = limit > 0 ? Math.min(limit, 6) : 0;
    const picks = cap ? ranked.slice(0, cap) : ranked;
    const expanded = !cap;

    if (liveEl) {
      liveEl.hidden = false;
      liveEl.classList.toggle('is-expanded', expanded);
    }
    if (picksMount) picksMount.classList.toggle('v-finder__picks--all', expanded);

    const gemBit = state.gem ? state.gem : 'any stone';
    const goldBit = state.gold ? state.gold : 'any gold';
    let status = '';
    if (!total) {
      status = `No ${type} match ${[state.gem, state.gold].filter(Boolean).join(' · ') || 'that combo'} yet.`;
    } else if (expanded) {
      status = `${total} ${type} in ${gemBit} + ${goldBit}`;
    } else {
      status = `${Math.min(picks.length, total)} of ${total} ${type} with ${gemBit} — pick a gold to see every match`;
    }
    if (liveStatus) liveStatus.textContent = status;

    if (picksMount) {
      picksMount.replaceChildren();
      picks.forEach(({ product: p, hit }, i) => {
        const a = document.createElement('a');
        a.className = 'v-finder__pick is-in';
        a.href = hit.id ? `${p.url}${p.url.includes('?') ? '&' : '?'}variant=${hit.id}` : p.url;
        a.style.setProperty('--i', String(i));
        const matchedOpts = hit.options || [];
        let comboLabel = [state.gem, state.gold].filter(Boolean).join(' · ');
        if (matchedOpts.length >= 2) comboLabel = matchedOpts.slice(0, 2).join(' · ');
        else if (matchedOpts.length === 1) comboLabel = matchedOpts[0];
        a.innerHTML = `
          <span class="v-finder__pick-media">
            ${hit.image ? `<img src="${escapeAttr(hit.image)}" alt="${escapeAttr(p.title || '')}" width="360" height="360" loading="lazy" decoding="async">` : ''}
          </span>
          <span class="v-finder__pick-meta">
            <span class="v-finder__pick-name">${escapeHtml(p.title || '')}</span>
            ${comboLabel ? `<span class="v-finder__pick-combo">${escapeHtml(comboLabel)}</span>` : ''}
            <span class="v-finder__pick-price">${escapeHtml(hit.price || p.price || '')}</span>
          </span>
        `;
        picksMount.appendChild(a);
      });
    }

    root.classList.add('has-results');
  }

  function finish() {
    updateLive(state.gold || !state.gem ? 0 : 6);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  function filterGrid() {
    if (!grid) return;
    const cards = grid.querySelectorAll('.v-finder-card-wrap');
    let visible = 0;
    cards.forEach((card) => {
      const gems = (card.dataset.gems || '').split(',').filter(Boolean).map(normalize);
      const golds = (card.dataset.golds || '').split(',').filter(Boolean).map(normalize);
      const gemOk =
        !state.gem ||
        gems.some((g) => optionHasFamily(g, state.gem)) ||
        gems.length === 0;
      const goldOk =
        !state.gold ||
        golds.some((g) => g === normalize(state.gold) || g.includes(normalize(state.gold).replace(' gold', ''))) ||
        golds.length === 0;
      const show = gemOk && goldOk;
      card.hidden = !show;
      card.classList.toggle('is-finder-match', show);
      card.classList.toggle('is-finder-dim', !show);
      if (show) visible += 1;
    });
    grid.querySelectorAll('[data-finder-editorial]').forEach((el) => {
      el.hidden = !!(state.gem || state.gold);
    });
    const note = document.querySelector('[data-veroni-grid-count]');
    if (note && (state.gem || state.gold)) {
      note.textContent = `Showing ${visible} match${visible === 1 ? '' : 'es'} for your edit`;
    }
  }

  function clearFilter() {
    state.gem = null;
    state.gold = null;
    if (chipsMount) {
      chipsMount.replaceChildren();
      chipsMount.hidden = true;
    }
    if (!grid) return;
    grid.querySelectorAll('.v-finder-card-wrap').forEach((card) => {
      card.hidden = false;
      card.classList.remove('is-finder-match', 'is-finder-dim');
    });
    grid.querySelectorAll('[data-finder-editorial]').forEach((el) => {
      el.hidden = false;
    });
    root.classList.remove('has-results');
  }

  document.addEventListener('veroni:grid-appended', () => {
    if (state.gem || state.gold) filterGrid();
  });

  root.querySelector('[data-finder-start]')?.addEventListener('click', () => {
    showStep('gem');
    renderGems();
    scrollToNext(steps.gem);
  });
  root.querySelector('[data-finder-skip]')?.addEventListener('click', () => {
    clearFilter();
    document.getElementById('veroni-collection-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  root.querySelector('[data-finder-any-gem]')?.addEventListener('click', () => {
    state.gem = null;
    if (gemMount) gemMount.querySelectorAll('.v-finder__choice').forEach((b) => b.classList.remove('is-selected'));
    showStep('gold');
    renderGolds();
    updateLive(6);
    scrollToNext(steps.gold);
  });
  root.querySelector('[data-finder-any-gold]')?.addEventListener('click', () => {
    state.gold = null;
    if (goldMount) goldMount.querySelectorAll('.v-finder__choice').forEach((b) => b.classList.remove('is-selected'));
    updateLive(0);
    scrollToNext(liveEl);
  });
  root.querySelector('[data-finder-restart]')?.addEventListener('click', () => {
    clearFilter();
    if (liveEl) liveEl.hidden = true;
    if (steps.gold) steps.gold.hidden = true;
    showStep('intro');
  });
  root.querySelector('[data-finder-scroll-grid]')?.addEventListener('click', () => {
    document.getElementById('veroni-collection-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  function variantImage(v, fallback) {
    const feat = v && v.featured_image;
    if (feat && feat.src) return feat.src;
    if (v && v.image) return v.image;
    return fallback || '';
  }

  function formatAjaxPrice(raw) {
    if (raw == null || raw === '') return '';
    const text = String(raw);
    if (text.indexOf('$') === 0) return text.replace(/\.00$/, '');
    const n = Number(text);
    if (!Number.isFinite(n)) return text;
    const dollars = text.indexOf('.') === -1 && n >= 1000 ? n / 100 : n;
    return '$' + (Math.round(dollars) === dollars ? String(Math.round(dollars)) : dollars.toFixed(2).replace(/\.00$/, ''));
  }

  function mapAjaxProduct(raw) {
    if (!raw || !raw.handle) return null;
    const gems = [];
    const golds = [];
    (raw.options || []).forEach((opt) => {
      const on = String(opt.name || '').toLowerCase();
      const values = opt.values || [];
      if (on === 'gemstone' || on === 'colored stone' || on === 'stone') {
        values.forEach((v) => gems.push(v));
      } else if (on === 'gold color' || on === 'color' || on === 'metal') {
        values.forEach((v) => golds.push(v));
      }
    });
    const fallbackImg = (raw.images && raw.images[0] && raw.images[0].src) || '';
    return {
      id: raw.id,
      handle: raw.handle,
      title: String(raw.title || '').split('|')[0].trim(),
      url: '/products/' + raw.handle,
      image: fallbackImg,
      price: raw.variants && raw.variants[0] ? formatAjaxPrice(raw.variants[0].price) : '',
      gems,
      golds,
      variants: (raw.variants || []).map((v) => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        title: v.title,
        image: variantImage(v, fallbackImg),
        price: formatAjaxPrice(v.price),
      })),
    };
  }

  async function hydrateCatalog() {
    const colUrl = root.dataset.collectionUrl;
    if (!colUrl) return;
    const seen = new Set(catalog.map((p) => p.handle).filter(Boolean));
    let page = 1;
    let added = 0;
    while (page <= 12) {
      let data;
      try {
        const res = await fetch(colUrl.replace(/\/$/, '') + '/products.json?limit=250&page=' + page, {
          credentials: 'same-origin',
        });
        if (!res.ok) break;
        data = await res.json();
      } catch (err) {
        break;
      }
      const batch = (data && data.products) || [];
      if (!batch.length) break;
      batch.forEach((raw) => {
        const mapped = mapAjaxProduct(raw);
        if (!mapped || !mapped.handle || seen.has(mapped.handle)) return;
        seen.add(mapped.handle);
        catalog.push(mapped);
        added += 1;
      });
      if (batch.length < 250) break;
      page += 1;
    }
    if (!added) return;
    if (state.gem || state.gold) {
      if (!state.gold && steps.gem && !steps.gem.hidden) renderGems();
      renderGolds();
      updateLive(state.gold ? 0 : 6);
    }
  }

  if (!catalog.length) {
    root.querySelector('[data-finder-start]')?.setAttribute('disabled', 'true');
  }

  hydrateCatalog();
})();
